import { useEffect, useState } from 'react';
import {
  evaluateUpsell,
  interactUpsell,
  type EvaluateUpsellBody,
  type UpsellSuggestionResult,
} from '@/lib/api/customer-status-upsell.js';

type Props = {
  context: EvaluateUpsellBody;
  enabled?: boolean;
  onAddService?: (suggestion: UpsellSuggestionResult) => void;
  mode?: 'inline' | 'checkout';
};

export function UpsellSuggestionsPanel({
  context,
  enabled = true,
  onAddService,
  mode = 'inline',
}: Props) {
  const [items, setItems] = useState<UpsellSuggestionResult[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [done, setDone] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const key = JSON.stringify({
    showAt: context.showAt,
    ticketId: context.ticketId,
    customerId: context.customerId,
    devices: context.devices,
    repairs: context.repairs,
  });

  useEffect(() => {
    if (!enabled) return;
    if (!context.devices?.length && !context.ticketId) return;
    if (!context.repairs?.length && !context.ticketId) return;
    let cancelled = false;
    setLoading(true);
    void evaluateUpsell(context)
      .then((data) => {
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  async function act(
    suggestion: UpsellSuggestionResult,
    outcome: 'acted_on' | 'dismissed' | 'remind_at_checkout' | 'not_applicable',
  ) {
    const price =
      typeof suggestion.actionConfig?.price === 'number'
        ? suggestion.actionConfig.price
        : undefined;
    await interactUpsell({
      suggestionId: suggestion.id,
      ticketId: context.ticketId,
      customerId: context.customerId,
      outcome,
      revenueAdded: outcome === 'acted_on' ? price : 0,
    }).catch(() => null);

    if (
      outcome === 'acted_on' &&
      (suggestion.actionType === 'add_service' || suggestion.actionType === 'add_inventory')
    ) {
      onAddService?.(suggestion);
      setDone((d) => ({ ...d, [suggestion.id]: 'added' }));
    } else {
      setDone((d) => ({ ...d, [suggestion.id]: outcome }));
    }
  }

  const visible = items.filter((i) => !done[i.id] || done[i.id] === 'added');
  if (!enabled || loading || visible.length === 0) return null;

  return (
    <div
      className={`rounded-xl border ${
        mode === 'checkout' ? 'border-amber-300 bg-amber-50' : 'border-amber-300 bg-amber-50'
      } p-4`}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-amber-900">
          Suggestions
        </h3>
        {mode === 'checkout' ? (
          <button
            type="button"
            className="text-xs font-medium text-amber-800 underline"
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? 'Expand' : 'Collapse all'}
          </button>
        ) : null}
      </div>
      {!collapsed
        ? visible.map((s) => (
            <div
              key={s.id}
              className="mb-3 rounded-lg border border-amber-200 bg-white/80 p-3 last:mb-0"
            >
              {done[s.id] === 'added' ? (
                <p className="text-sm font-medium text-emerald-700">✓ {s.title} added</p>
              ) : (
                <>
                  <p className="font-semibold text-slate-900">{s.title}</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{s.message}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {s.actionType === 'add_service' || s.actionType === 'add_inventory' ? (
                      <button
                        type="button"
                        className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white"
                        onClick={() => void act(s, 'acted_on')}
                      >
                        {mode === 'checkout'
                          ? s.actionLabel || 'Add to Order'
                          : s.actionLabel || 'Add service'}
                      </button>
                    ) : null}
                    {mode === 'checkout' ? (
                      <>
                        <button
                          type="button"
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                          onClick={() => void act(s, 'remind_at_checkout')}
                        >
                          Noted
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                          onClick={() => void act(s, 'dismissed')}
                        >
                          Skip
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                          onClick={() => void act(s, 'not_applicable')}
                        >
                          Not Applicable
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                        onClick={() => void act(s, 'dismissed')}
                      >
                        Not Now
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))
        : null}
    </div>
  );
}
